// Bounded, keyed fan-out (HUBX-8477).
//
// The scheduling half of the multi-row save, with nothing about items in it: run one
// Observable per input, at most `limit` at a time, and never two whose key is equal at
// the same time. Kept apart from the service so the two rules the ticket states — "up to
// 5 in flight" and "never two rows that share an ItemId" — can be tested with plain
// Subjects, one step at a time, instead of through HTTP.
//
// Why a hand-rolled scheduler rather than mergeMap(fn, 5): mergeMap bounds concurrency but
// cannot hold an input back because of what else is running. Two rows on the same master
// contend on the `Items` rowversion, and the second would come back 412 for a conflict
// the user did not cause. Waiting for the first is the only correct order, and it needs a
// queue that can skip over a blocked input to the next runnable one.

import { Observable, Subscription } from 'rxjs';

/** Run `run(item)` for every item, at most `limit` concurrently, never two with the same
 *  `keyOf` concurrently. Emits `{ item, result }` for every value an inner Observable
 *  emits, in completion order, and completes once every inner has completed.
 *
 *  Order: FIFO among runnable items. An item whose key is busy is skipped, not the whole
 *  queue — it starts as soon as its key frees, ahead of anything queued behind it.
 *
 *  Errors: `run` is expected to turn a failure into a value (the batch save maps every
 *  HTTP error to a per-row outcome), because one failure must not stop the others. An
 *  inner that errors anyway is a programming error and errors the whole fan-out.
 *
 *  Unsubscribing cancels whatever is still in flight and starts nothing more. */
export function fanOut<T, R>(
  items: readonly T[],
  keyOf: (item: T) => string,
  run: (item: T) => Observable<R>,
  limit: number
): Observable<{ item: T; result: R }> {
  return new Observable<{ item: T; result: R }>(subscriber => {
    const queue = [...items];
    const busy = new Set<string>();
    const inFlight = new Subscription();
    let active = 0;

    const finishIfDone = (): void => {
      if (!queue.length && active === 0) {
        subscriber.complete();
      }
    };

    const pump = (): void => {
      while (active < Math.max(1, limit) && !subscriber.closed) {
        const index = queue.findIndex(candidate => !busy.has(keyOf(candidate)));
        if (index < 0) {
          break;
        }
        const [item] = queue.splice(index, 1);
        const key = keyOf(item);
        busy.add(key);
        active++;
        inFlight.add(run(item).subscribe({
          next: result => subscriber.next({ item, result }),
          error: err => subscriber.error(err),
          complete: () => {
            busy.delete(key);
            active--;
            pump();
            finishIfDone();
          }
        }));
      }
    };

    pump();
    finishIfDone();
    return () => inFlight.unsubscribe();
  });
}
